/**
 * ledger.state.ts — the view-model `<order-refunds>` renders: the open
 * order's refund ledger in the order an operator must read it, and how many of
 * its rows are still waiting for a person.
 *
 * The classification rides BESIDE the entity row rather than flattening it, so
 * every state the model grows stays visible to the surface reasoning over it —
 * the same shape `RefundTicketRow` already takes for a ticket.
 *
 * Beside that list sits the other reading of the same one ledger: what a
 * ticket's own row is allowed to say about money. `cancelled` is an outcome,
 * and the operator looking at a ticket is owed the CAUSE — a cancelled ticket
 * a ledger row names was refunded by that row, a cancelled ticket no row names
 * was purged, and a still-valid ticket a row names is money in flight against
 * a seat that still admits at the door. Three sentences behind one status
 * token, read off the one cell `<order-refunds>` below the rows reads.
 *
 * Ordering is the list half's whole job. A row nobody is working on any more must
 * never sit below three receipts: terminal rows first, then a row the ladder is
 * still retrying against a refusal it has already recorded, then the ordinary
 * in-flight rows, then the receipts, newest first. A row that is `claiming` and
 * carrying a refusal is deliberately its own rank rather than folded in with
 * the other in-flight rows: the provider has already said no to it, and it is
 * the only rank on this list an operator can still turn around before it
 * becomes terminal.
 */
import { computed } from "@mhmo91/schmancy/state";
import type { Ticket } from "@momo/types";
import { consoleTickets } from "../console.state.ts";
import {
	openOrderId,
	orderRefundLedger,
	type RefundRow,
} from "../order-ops.state.ts";

/** How loudly one row reads, and where it sits. `needsAPerson` is the runbook's
 *  own sentence for a `failed` row — nothing in the code will ever clear it. */
export type RefundRowKind =
	| "needsAPerson"
	| "refusedButRetrying"
	| "underWay"
	| "settled";

export type OrderRefundEntry = {
	readonly kind: RefundRowKind;
	readonly row: RefundRow;
};

export type OrderRefundsView = {
	readonly orderId: string;
	readonly entries: readonly OrderRefundEntry[];
	/** How many rows nothing in the system will clear on its own. */
	readonly needsAPerson: number;
};

const RANK: Readonly<Record<RefundRowKind, number>> = {
	needsAPerson: 0,
	refusedButRetrying: 1,
	underWay: 2,
	settled: 3,
};

export const orderRefundsView = computed((): OrderRefundsView | null => {
	const ledger = orderRefundLedger.value;
	if (!ledger) return null;
	const entries = ledger.rows
		.map(
			(row): OrderRefundEntry => ({
				kind:
					row.status === "failed"
						? "needsAPerson"
						: row.status === "claiming" && row.providerRefusal
							? "refusedButRetrying"
							: row.status === "settled"
								? "settled"
								: "underWay",
				row,
			}),
		)
		.sort(
			(a, b) =>
				RANK[a.kind] - RANK[b.kind] ||
				(b.row.settledAt?.toMillis() ?? 0) - (a.row.settledAt?.toMillis() ?? 0),
		);
	return {
		orderId: ledger.orderId,
		entries,
		needsAPerson: entries.filter((e) => e.kind === "needsAPerson").length,
	};
});

/** What one ticket's own row may say about money, from the two documents that
 *  between them know it: the ticket, which carries `cancelled` and no reason
 *  at all, and this order's ledger, which carries the reason, the amount, the
 *  day and the correction number.
 *
 *  `unanswered` is its own reading rather than folding into "no refund on
 *  record": the ledger cell is null until a listener answers, and a row that
 *  said "no refund" in that gap would state as fact the one thing the screen
 *  does not yet know.
 *
 *  `amountCents` is present only where the claim names this ticket ALONE. A
 *  claim covering several seats has one amount for all of them, and dividing
 *  it per seat would invent a figure no document holds — so the row states the
 *  fact and the count, and the money stays with the claim in `<order-refunds>`
 *  below, where it is written down once. */
export type TicketRefundReading =
	/** A settled claim names this ticket. The money went back; the seat is void. */
	| {
			readonly kind: "refunded";
			readonly amountCents: number | null;
			readonly settledAt: Date | null;
			readonly alsoCovers: number;
	  }
	/** The money moved and the provider has still to confirm it. */
	| { readonly kind: "confirming" }
	/** A claim holds this ticket and the provider has not answered yet. The
	 *  seat is still valid, so it still admits at the door — which is the part
	 *  an operator gets wrong if the row only says "refund". */
	| { readonly kind: "underWay"; readonly refused: boolean }
	/** Nothing came back and nothing in the system will try again. */
	| { readonly kind: "failed"; readonly stillAdmits: boolean }
	/** Cancelled with no refund row anywhere — the purge path, the other and
	 *  only other writer of this status. */
	| { readonly kind: "cancelledNoRefund" }
	/** Cancelled, and the ledger has not answered yet. */
	| { readonly kind: "unanswered" };

/** A ticket can be held by at most one uncleared row, but a failed row keeps
 *  holding its tickets forever, so a settled row beside it is the one that
 *  states what happened. Rank settles that without depending on write order. */
const TICKET_ROW_RANK: Readonly<Record<RefundRow["status"], number>> = {
	settled: 0,
	pending: 1,
	claiming: 2,
	failed: 3,
};

export function readTicketRefund(
	ticket: { readonly id: string; readonly status: Ticket.Ticket["status"] },
	ledger: readonly RefundRow[] | null,
): TicketRefundReading | null {
	if (ticket.status === "redeemed") return null;
	if (ledger === null)
		return ticket.status === "cancelled" ? { kind: "unanswered" } : null;
	const row = ledger
		.filter((r) => r.ticketIds.includes(ticket.id))
		.sort((a, b) => TICKET_ROW_RANK[a.status] - TICKET_ROW_RANK[b.status])[0];
	if (!row)
		return ticket.status === "cancelled" ? { kind: "cancelledNoRefund" } : null;
	switch (row.status) {
		case "settled":
			return {
				kind: "refunded",
				amountCents: row.ticketIds.length === 1 ? row.amount : null,
				settledAt: row.settledAt?.toDate() ?? null,
				alsoCovers: row.ticketIds.length - 1,
			};
		case "pending":
			return { kind: "confirming" };
		case "claiming":
			return { kind: "underWay", refused: !!row.providerRefusal };
		case "failed":
			return { kind: "failed", stillAdmits: ticket.status === "valid" };
	}
}

/** One reading per ticket of the order whose sheet is open — the map
 *  `<order-detail>`'s row reads, so the sheet derives nothing itself. Keyed on
 *  the open order rather than on the ledger's, so the tickets are the ones on
 *  screen even while the ledger is still answering: those rows read
 *  `unanswered` and swap to their settled sentence in place. */
export const ticketRefundReadings = computed(
	(): ReadonlyMap<string, TicketRefundReading | null> => {
		const orderId = openOrderId.value;
		if (orderId === null) return new Map();
		const ledger = orderRefundLedger.value;
		const rows = ledger?.orderId === orderId ? ledger.rows : null;
		return new Map(
			consoleTickets.value
				.filter((t) => t.orderId === orderId)
				.map((t) => [t.id, readTicketRefund(t, rows)]),
		);
	},
);
